import { useMemo, useState } from "react";
import {
  Box,
  Collapse,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { NodeDef, WorkflowToolCatalog } from "./workflow.types";

interface NodePaletteProps {
  catalog: WorkflowToolCatalog | null;
  loading: boolean;
  hint: string | null;
  onAdd: (def: NodeDef) => void;
}

const SECTIONS: { key: "triggers" | "actions" | "logic"; title: string; dotClass: string }[] = [
  { key: "triggers", title: "Triggers", dotClass: "wf-palette-dot--trigger" },
  { key: "actions", title: "Actions", dotClass: "wf-palette-dot--action" },
  { key: "logic", title: "Logic", dotClass: "wf-palette-dot--logic" },
];

function matches(def: NodeDef, query: string) {
  if (!query) return true;
  const q = query.toLowerCase();
  return def.label.toLowerCase().includes(q) || def.description.toLowerCase().includes(q);
}

export default function NodePalette({ catalog, loading, hint, onAdd }: NodePaletteProps) {
  const [query, setQuery] = useState("");
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    triggers: true,
    actions: true,
    logic: true,
  });

  const filtered = useMemo(() => {
    if (!catalog) return null;
    const result: WorkflowToolCatalog = { triggers: [], actions: [], logic: [], conditionFields: [] };
    SECTIONS.forEach(({ key }) => {
      result[key] = (catalog[key] || []).filter((def) => matches(def, query));
    });
    return result;
  }, [catalog, query]);

  const toggleSection = (key: string) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Box sx={{ p: 1.5, height: "100%", overflowY: "auto" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Add a step
      </Typography>
      <TextField
        size="small"
        fullWidth
        placeholder="Search steps…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1.5 }}
      />

      {hint && (
        <Box
          sx={{
            fontSize: 12,
            color: "warning.dark",
            bgcolor: "warning.light",
            borderRadius: 1,
            px: 1,
            py: 0.5,
            mb: 1.5,
          }}
        >
          {hint}
        </Box>
      )}

      {loading && (
        <Typography variant="body2" color="text.secondary">
          Loading steps…
        </Typography>
      )}

      {filtered &&
        SECTIONS.map(({ key, title, dotClass }) => {
          const defs = filtered[key];
          if (defs.length === 0 && query) return null;
          return (
            <Box key={key} sx={{ mb: 1 }}>
              <ListItemButton
                dense
                onClick={() => toggleSection(key)}
                sx={{ borderRadius: 1, px: 1 }}
              >
                <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, letterSpacing: 0.4 }}>
                  {title.toUpperCase()} ({defs.length})
                </Typography>
                {openSections[key] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </ListItemButton>
              <Collapse in={openSections[key] ?? true}>
                <List disablePadding sx={{ px: 0.5 }}>
                  {defs.map((def) => (
                    <div
                      key={def.subtype}
                      className="wf-palette-card"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("application/x-workflow-node", JSON.stringify(def));
                        e.dataTransfer.effectAllowed = "copy";
                      }}
                      onClick={() => onAdd(def)}
                    >
                      <div className="wf-palette-card__label">
                        <span className={`wf-palette-dot ${dotClass}`} />
                        {def.label}
                      </div>
                      <div className="wf-palette-card__desc">{def.description}</div>
                    </div>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
    </Box>
  );
}
